import { Component, OnInit } from '@angular/core';
import { SettingsService } from '../settings/settings.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';

class ConditionalErrorMatcher implements ErrorStateMatcher {
  constructor(private fn: () => boolean) {}
  isErrorState(_c: FormControl | null, _f: FormGroupDirective | NgForm | null): boolean {
    return this.fn();
  }
}

const COMM_BASE = 128_500;
const COMM_RATE = 0.005;
const ROUND_STEP = 500;

const PRORRATE_DIVISOR = 30;

function roundToStep(x: number, step = ROUND_STEP) {
  return Math.round(x / step) * step;
}

const RATE_NORMALIZATION = 30 / 32;


function isApprox(a: number, b: number, eps = 1e-9) {
  return Math.abs(a - b) < eps;
}

function trunc1(x: number): number {
  return Math.trunc((x ?? 0) * 10) / 10;
}

function round1(x: number): number {
  return Math.round((x + Number.EPSILON) * 10) / 10;
}

@Component({
  selector: 'app-landing-inversiones',
  standalone: false,
  templateUrl: './landing-inversiones.component.html',
  styleUrl: './landing-inversiones.component.less',
})
export class LandingInversionesComponent implements OnInit {

  // ── Error matchers ────────────────────────────────────────────────────────
  rutErrorMatcher = new ConditionalErrorMatcher(() =>
    this.rutStatus === 'error' || this.rutStatus === 'blocked'
  );
  businessNameErrorMatcher = new ConditionalErrorMatcher(() => !this.businessName);
  onboardingNombreErrorMatcher = new ConditionalErrorMatcher(() =>
    !!this.onboardingNombreError && this.onboardingTouched
  );
  onboardingEmailErrorMatcher = new ConditionalErrorMatcher(() =>
    !!this.onboardingEmailError && this.onboardingTouched
  );
  onboardingTelefonoErrorMatcher = new ConditionalErrorMatcher(() =>
    !!this.onboardingTelefonoError && this.onboardingTouched
  );
  deudorRutErrorMatcher = new ConditionalErrorMatcher(() =>
    this.deudorRutStatus === 'invalid'
  );

  // ── Hero background ───────────────────────────────────────────────────────
  randomImage = '';
  private readonly heroImages = [
    '../../assets/images/33.jpg',
    '../../assets/images/3.jpg',
    '../../assets/images/4.jpg',
    '../../assets/images/7.jpg',
    '../../assets/images/9.jpg',
    '../../assets/images/12.jpg',
    '../../assets/images/14.jpg',
    '../../assets/images/17.jpg',
    '../../assets/images/23.jpg',
    '../../assets/images/2.jpg',
    '../../assets/images/15.jpg',
    '../../assets/images/26.jpg',
  ];

  // ── RUT step ──────────────────────────────────────────────────────────────
  showRutStep = true;
  rut = '';
  rutLoading = false;
  rutStatus: 'idle' | 'ok' | 'blocked' | 'error' = 'idle';
  rutErrorMsg = '';
  businessName = '';
  businessNameReadonly = false;
  private lastValidatedRut = '';
  deudorRut = '';
  deudorRutStatus: 'idle' | 'valid' | 'invalid' | 'loading' = 'idle';
  promedioDias: number | null = null;
  private lastValidatedDeudorRut = '';
  tramoMessage = '';
  tramoNumber: number | null = null;
  tramoRangeMin: number | null = null;
  tramoRangeMax: number | null = null;
  isPersonaNatural = false;
  get tramoAceptado(): boolean | null {
    if (this.tramoNumber === null) return null;
    return this.tramoNumber >= 4;
  }
  prospectResult: 'DISPONIBLE' | 'PROSPECTO' | null = null;
  prospectCompanyId: number | null = null;
  prospectPercent = 0;
  simulandoLoading = false;

  // ── Simulator ────────────────────────────────────────────────────────────
  cotizacionAmount: number = 1_000_000;
  margenPercent: number = 30;
  facturaNumero: string = '';

  get invoiceAmount(): number {
    return Math.round(this.cotizacionAmount * (1 + this.margenPercent / 100));
  }

  termDays: number = 30;
  advancePercent: number = 90;

  monthlyRatePercent: number = 1.8;
  effectiveMonthlyRatePercent: number = 1.8;

  ivaPercent = 19;
  gastosOperacionales = 30_000;
  incluirTasa015 = false;
  tasa015Pct = 0.15;

  financedAmount: number = 0;
  financingCost: number = 0;
  commissionCost: number = 0;
  vatCost: number = 0;
  receivesToday: number = 0;
  receivesAtMaturity: number = 0;

  manualRate = false;

  trancheRatePercent = 3.1;

  ivaBaseMode: 'comision' | 'comisionYGastos' | 'todo' = 'todo';

  rateMode: 'auto' | 'manual' = 'manual';
  useOperationalExpense = false;

  competitorAvgPct = 2.55;

  // ── Typewriter ────────────────────────────────────────────────────────────
  typewriterDisplay = '';
  typewriterDone = false;
  private readonly typewriterFull = 'Rápido, simple y sin compromiso.';
  private readonly typewriterSplit = 'Rápido, simple y '.length;

  get typewriterPart1(): string {
    return this.typewriterDisplay.slice(0, Math.min(this.typewriterDisplay.length, this.typewriterSplit));
  }

  get typewriterPart2(): string {
    return this.typewriterDisplay.length > this.typewriterSplit
      ? this.typewriterDisplay.slice(this.typewriterSplit)
      : '';
  }

  private startTypewriter(): void {
    const full = this.typewriterFull;
    let i = 0;
    let lastTime = 0;
    // natural variance: pauses slightly on punctuation/spaces, faster on regular chars
    const getDelay = (char: string): number => {
      if (char === '.' || char === ',') return 120 + Math.random() * 60;
      if (char === ' ') return 55 + Math.random() * 20;
      return 38 + Math.random() * 22;
    };
    let nextDelay = getDelay(full[0] ?? '');

    const tick = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp;

      if (timestamp - lastTime >= nextDelay) {
        this.typewriterDisplay = full.slice(0, ++i);
        lastTime = timestamp;
        nextDelay = getDelay(full[i] ?? '');
      }

      if (i < full.length) {
        requestAnimationFrame(tick);
      } else {
        this.typewriterDone = true;
      }
    };

    requestAnimationFrame(tick);
  }

  constructor(
    public settings: SettingsService,
  ) {
  }

  ngOnInit() {
    this.randomImage = this.heroImages[Math.floor(Math.random() * this.heroImages.length)];
    setTimeout(() => this.startTypewriter(), 400);
    this.rateMin = this.getMinRateForTerm(this.termDays);
    this.recalcAll();
  }

  rateMin = 1.5;
  rateMax = 2.8;

  // ── RUT formatting & validation ───────────────────────────────────────────

  private isValidRut(rut: string): boolean {
    const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (clean.length < 2) return false;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    if (!/^\d+$/.test(body)) return false;
    let sum = 0;
    let mul = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i], 10) * mul;
      mul = mul === 7 ? 2 : mul + 1;
    }
    const rem = 11 - (sum % 11);
    const expected = rem === 11 ? '0' : rem === 10 ? 'K' : String(rem);
    return dv === expected;
  }

  private formatRut(raw: string): string {
    const clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
    if (clean.length <= 1) return clean;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    return `${body}-${dv}`;
  }

  onRutInput(raw: string) {
    this.rut = this.formatRut(raw);
    this.rutStatus = 'idle';
    this.rutErrorMsg = '';
    this.tramoMessage = '';
    this.tramoNumber = null;
    this.tramoRangeMin = null;
    this.tramoRangeMax = null;
    this.isPersonaNatural = false;
    this.businessName = '';
    this.prospectResult = null;
    this.lastValidatedRut = '';
  }

  onRutBlur() {
    if (this.rut.length < 3) return;
    if (this.rut === this.lastValidatedRut) return;
    if (!this.isValidRut(this.rut)) {
      this.rutStatus = 'error';
      this.rutErrorMsg = 'El RUT ingresado no es válido. Verifica el dígito verificador.';
      return;
    }
    this.validateRut();
  }

  async validateRut() {
    if (!this.rut || this.rut.length < 3 || !this.isValidRut(this.rut)) return;
    this.rutLoading = true;
    this.rutStatus = 'idle';
    this.rutErrorMsg = '';
    this.tramoMessage = '';
    this.tramoNumber = null;
    this.tramoRangeMin = null;
    this.tramoRangeMax = null;
    this.isPersonaNatural = false;
    this.businessName = '';
    this.prospectResult = null;
    this.prospectCompanyId = null;

    try {
      const bd: any = await this.settings.get_query(1, `tubovalidaterut/?rut=${this.rut}`);
      const data = bd._body;
      const result = data.result;

      if (result === 'BLOQUEADO') {
        this.rutStatus = 'blocked';
        this.rutErrorMsg = 'Este RUT se encuentra bloqueado por Simpli y no puede continuar con el proceso de simulación. Por favor ingrese otro RUT.';
        return;
      }

      if (result === 'DISPONIBLE') {
        this.prospectResult = 'DISPONIBLE';
        await this.loadProspectoInfoSii();
        await this.loadTramoSii();
        this.rutStatus = 'ok';
        this.lastValidatedRut = this.rut;
        return;
      }

      if (result === 'PROSPECTO' || result === 'CLIENTE') {
        this.prospectResult = 'PROSPECTO';
        this.prospectCompanyId = data.company_id;
        this.prospectPercent = data.info?.percent ?? 0;
        await this.loadProspectoForm();
        await this.loadTramoSii();
        this.rutStatus = 'ok';
        this.lastValidatedRut = this.rut;
        return;
      }

      this.rutStatus = 'error';
    } catch {
      this.rutStatus = 'error';
      this.rutErrorMsg = 'No se pudo validar el RUT. Intente nuevamente.';
    } finally {
      this.rutLoading = false;
    }
  }

  private async loadProspectoInfoSii() {
    try {
      const bd: any = await this.settings.get_query(1, `tubo/prospecto-info-sii/?rut=${this.rut}`);
      const info = bd._body?.success?.info;
      const razonSocial = (info?.razon_social as string | undefined)?.trim() ?? '';
    if (info?.ok && razonSocial && !razonSocial.includes('**')) {
        this.businessName = razonSocial.toUpperCase();
        this.businessNameReadonly = false;
      } else {
        this.businessName = '';
        this.businessNameReadonly = false;
      }
    } catch {
      this.businessName = '';
      this.businessNameReadonly = false;
    }
  }

  private async loadProspectoForm() {
    try {
      const bd: any = await this.settings.get_query(1, `tubo/prospecto-form/${this.prospectCompanyId}/`);
      const prospecto = bd._body?.success?.prospecto;
      if (prospecto?.business_name) {
        this.businessName = (prospecto.business_name as string).toUpperCase();
        this.businessNameReadonly = true;
      }
    } catch {
      this.businessName = '';
      this.businessNameReadonly = false;
    }
  }

  private async loadTramoSii() {
    this.isPersonaNatural = false;
    try {
      const bd: any = await this.settings.get_query(1, `tramosii/?rut=${this.rut}`);
      const data = bd._body;
      if (data?.tramo) {
        this.tramoNumber = Number(data.tramo) || null;
        this.tramoRangeMin = data.range_sale_min ?? null;
        this.tramoRangeMax = data.range_sale_max ?? null;
        this.tramoMessage = `Tramo ${data.tramo} - ${data.description}`;
      }
    } catch (err: any) {
      this.tramoNumber = null;
      this.tramoRangeMin = null;
      this.tramoRangeMax = null;
      if (err?.status === 404 || err?.statusCode === 404) {
        this.isPersonaNatural = true;
        this.tramoMessage = '⚠ Esta empresa no tiene un tramo SII asignado. Puede continuar con el proceso.';
      } else {
        this.tramoMessage = '';
      }
    }
  }

  get fechaVencimiento(): string {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + this.termDays);
    const dia = fecha.getDay();
    if (dia === 6) fecha.setDate(fecha.getDate() + 2); // sábado → lunes
    if (dia === 0) fecha.setDate(fecha.getDate() + 1); // domingo → lunes
    return fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  get canSimular(): boolean {
    return this.rutStatus === 'ok' && this.rut.length >= 3;
  }

  async onSimular() {
    if (this.simulandoLoading || !this.canSimular) return;
    this.simulandoLoading = true;

    try {
      if (this.prospectResult === 'DISPONIBLE') {
        const payload = {
          rut: this.rut,
          nombreFantasia: '',
          business_name: this.businessName || 'Prospecto',
          giro: [],
          web: null,
          direccion: null,
          numero: null,
          reset: null,
          phone: null,
          email: null,
          region: null,
          comuna: null,
          origin: 13,
          tramo_sii: null,
          interest: 0,
        };
        const created: any = await this.settings.get_query(2, 'tubo/prospecto-form/', payload);
        const newCompanyId = created?._body?.success?.prospecto?.id;
        if (newCompanyId) {
          this.prospectCompanyId = newCompanyId;
          await this.settings.get_query(2, `tubo/ingram-simular/${newCompanyId}/`, {});
        }
      } else if (this.prospectResult === 'PROSPECTO' && this.prospectCompanyId) {
        // backend handles: 0-75% → reassign + update origin; 100% → update origin only
        await this.settings.get_query(2, `tubo/ingram-simular/${this.prospectCompanyId}/`, {});
      }

      this.showRutStep = false;
    } catch {
      // proceed to simulator even on registration error
      this.showRutStep = false;
    } finally {
      this.simulandoLoading = false;
    }
  }

  // ── Simulator logic ───────────────────────────────────────────────────────

  private getMinRateForTerm(_days: number): number {
    return 1.5;
  }

  setMargen(v: number) {
    this.margenPercent = Math.max(10, Math.min(70, +v || 10));
    this.invalidarSimulacion();
    this.recalcAll();
  }

  onRateChange(v: number) {
    const vNum = +v || 0;
    this.monthlyRatePercent = Math.min(vNum, this.rateMax);
    this.effectiveMonthlyRatePercent = this.monthlyRatePercent;
    this.rateMode = 'manual';
    this.invalidarSimulacion();
    this.recalcMontos();
  }

  private getComisionSimpli(montoFactura: number, montoFinanciado: number): number {
    if (montoFactura < 50_000_000) return 178_500;
    const raw = COMM_BASE + montoFinanciado * COMM_RATE;
    return roundToStep(raw);
  }

  private clampPct(p: number): number {
    return Math.max(0, Math.min(100, p));
  }

  private recalcAll() {
    if (this.rateMode === 'auto') {
      const mm = this.invoiceAmount;
      const base =
        mm <= 10_000_000 ? 3.1 :
        mm >= 10_000_000 && mm <= 20_000_000 ? 2.7 :
        mm >= 20_000_000 && mm <= 50_000_000 ? 2.5 :
        mm >= 50_000_000 && mm <= 120_000_000 ? 2.2 :
        2.0;
      this.effectiveMonthlyRatePercent = base;
      this.monthlyRatePercent = Math.max(this.rateMin, Math.min(this.rateMax, base));
    }
    this.recalcMontos();
  }

  private recalcMontos() {
    const montoFactura = Math.max(0, Math.trunc(this.invoiceAmount) || 0);
    const anticipo = this.clampPct(this.advancePercent) / 100;

    this.financedAmount = Math.round(montoFactura * anticipo);
    this.commissionCost = this.getComisionSimpli(montoFactura, this.financedAmount);

    const appliedRatePct = this.getAppliedRatePct();
    const tasaMensual = (appliedRatePct || 0) / 100;
    const tasaDiaria = tasaMensual / PRORRATE_DIVISOR;
    const intereses = Math.round(this.financedAmount * tasaDiaria * this.termDays);
    this.financingCost = intereses;

    this.vatCost = Math.round((this.commissionCost + this.gastosOperacionales) * (this.ivaPercent / 100));

    this.receivesToday = Math.round(
      this.financedAmount - this.financingCost - this.gastosOperacionales - this.commissionCost - this.vatCost
    );

    this.receivesAtMaturity = Math.round(montoFactura - this.financedAmount);
  }

  setAmount(v: number) {
    this.cotizacionAmount = v || 0;

    let base: number;
    if (v >= 120_000_000) { base = 1.8; }
    else if (v >= 50_000_000) { base = 2.0; }
    else if (v >= 20_000_000) { base = 2.2; }
    else if (v >= 10_000_000) { base = 2.5; }
    else { base = 2.8; }

    this.effectiveMonthlyRatePercent = Math.min(base, this.rateMax);
    this.monthlyRatePercent = Math.max(this.rateMin, Math.min(this.rateMax, base));
    this.rateMode = 'auto';
    this.recalcMontos();
  }

  onAmountInput(raw: string) {
    const clean = (raw || '').replace(/[^\d]/g, '');
    this.cotizacionAmount = +clean || 0;
    this.invalidarSimulacion();
    this.recalcAll();
  }

  setTermDays(v: number) {
    this.termDays = Math.max(0, +v || 0);
    this.invalidarSimulacion();
    const newMin = this.getMinRateForTerm(this.termDays);
    this.rateMin = newMin;
    if (this.monthlyRatePercent < newMin) {
      this.monthlyRatePercent = newMin;
      this.effectiveMonthlyRatePercent = newMin;
      this.recalcMontos();
    }
  }

  setAdvance(v: number) {
    this.advancePercent = Math.max(70, Math.min(100, +v || 0));
    this.invalidarSimulacion();
    this.recalcAll();
  }

  private get displayRatePct(): number {
    return this.effectiveMonthlyRatePercent;
  }

  get showSavingsBanner(): boolean {
    return this.displayRatePct <= 2.4;
  }

  private getAppliedRatePct(): number {
    const r = this.effectiveMonthlyRatePercent;
    const min = this.getMinRateForTerm(this.termDays);
    return isApprox(r, min) ? r * RATE_NORMALIZATION : r;
  }

  get savingsDeltaPct(): number {
    const min = this.getMinRateForTerm(this.termDays);
    if (isApprox(this.displayRatePct, min)) {
      return Math.max(0, round1(this.competitorAvgPct - this.displayRatePct));
    }
    const d = this.competitorAvgPctDisplay - this.displayRatePctDisplay;
    return Math.max(0, trunc1(d));
  }

  get showReceivesAtMaturity(): boolean {
    return (this.receivesAtMaturity ?? 0) > 0;
  }

  get displayRatePctDisplay(): number {
    return trunc1(this.displayRatePct);
  }

  get competitorAvgPctDisplay(): number {
    return trunc1(this.competitorAvgPct);
  }

  guardandoLoading = false;
  guardandoStep: 'idle' | 'validando' | 'guardando' = 'idle';
  simulacionGuardadaId: number | null = null;
  descargandoLoading = false;

  // Onboarding form
  showOnboardingForm = false;
  onboardingNombre = '';
  onboardingEmail = '';
  onboardingTelefono = '';
  onboardingTouched = false;
  registrandoLoading = false;
  onboardingRegistrado = false;

  get onboardingNombreError(): string {
    if (!this.onboardingTouched) return '';
    return !this.onboardingNombre.trim() ? 'El nombre es obligatorio.' : '';
  }

  get onboardingEmailError(): string {
    if (!this.onboardingTouched) return '';
    if (!this.onboardingEmail.trim()) return 'El correo es obligatorio.';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.onboardingEmail) ? '' : 'Ingresa un correo válido.';
  }

  get onboardingTelefonoError(): string {
    if (!this.onboardingTouched) return '';
    if (!this.onboardingTelefono.trim()) return 'El teléfono es obligatorio.';
    if (!/^\d+$/.test(this.onboardingTelefono)) return 'Solo se permiten números.';
    if (this.onboardingTelefono.length > 9) return 'Máximo 9 dígitos.';
    return '';
  }

  get onboardingFormValid(): boolean {
    return !this.onboardingNombreError && !this.onboardingEmailError && !this.onboardingTelefonoError
      && !!this.onboardingNombre.trim() && !!this.onboardingEmail.trim() && !!this.onboardingTelefono.trim();
  }

  onTelefonoInput(val: string) {
    this.onboardingTelefono = val.replace(/\D/g, '').slice(0, 9);
  }

  onboardingPortalEmail = '';
  onboardingPortalPassword = '';
  credencialesCopied = false;

  readonly portalUrl = 'https://clientes.simplilatam.com/';

  get credencialesTexto(): string {
    return `Email: ${this.onboardingPortalEmail}\nContraseña: ${this.onboardingPortalPassword}\n\nIngresa aquí: ${this.portalUrl}`;
  }

  copiarCredenciales() {
    navigator.clipboard.writeText(this.credencialesTexto).then(() => {
      this.credencialesCopied = true;
      setTimeout(() => { this.credencialesCopied = false; }, 2500);
    });
  }

  invalidarSimulacion() {
    if (this.simulacionGuardadaId !== null) {
      this.simulacionGuardadaId = null;
    }
  }

  abrirOnboarding() {
    this.showOnboardingForm = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!this.onboardingRegistrado) {
      this.onboardingTouched = false;
      this.onboardingNombre = '';
      this.onboardingEmail = '';
      this.onboardingTelefono = '';
      this.onboardingPortalEmail = '';
      this.onboardingPortalPassword = '';
    }
  }

  volverSimulacion() {
    this.showOnboardingForm = false;
  }

  async registrarOnboarding() {
    this.onboardingTouched = true;
    if (!this.onboardingFormValid || this.registrandoLoading || !this.prospectCompanyId) return;
    this.registrandoLoading = true;
    try {
      const email = this.onboardingEmail.trim();
      const password = this.rut;
      const payload = {
        name: this.onboardingNombre.trim(),
        rut: this.rut,
        cargo: null,
        fecha_nacimiento: null,
        email,
        participacion: null,
        profesion: null,
        direccion: null,
        whatsapp: null,
        estado_civil: null,
        file: null,
        experiencia: null,
        checks: [{ id: 4, value: 'Dar Acceso Portal Clientes Factoring' }],
        nacionalidad: null,
        genero: 0,
        numeros: [Number(this.onboardingTelefono)],
        rols: null,
      };
      const res: any = await this.settings.get_query(2, `linea/company-contact/${this.prospectCompanyId}/`, payload);
      if (res?.status === 200 || res?.status === 201) {
        const contactId: number = res._body?.success?.contact?.id;
        if (contactId) {
          this.settings.get_query(3, `company-contact-password/${contactId}/`, {
            password,
            re_password: password,
            mail: email,
          });
        }
        this.onboardingPortalEmail = email;
        this.onboardingPortalPassword = password;
        this.onboardingRegistrado = true;
        this.settings.snackBar.open('¡Contacto registrado con éxito!', 'Cerrar', {
          duration: 5000,
          panelClass: 'simpli-snack',
        });
      }
    } catch {
      // silently fail
    } finally {
      this.registrandoLoading = false;
    }
  }
  get guardandoMsg(): string {
    if (this.guardandoStep === 'validando') return 'Validando facturas...';
    if (this.guardandoStep === 'guardando') return 'Guardando simulación...';
    return 'Guardar simulación →';
  }

  private formatDateCL(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  private getFechaVencimientoDate(): Date {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + this.termDays);
    const dia = fecha.getDay();
    if (dia === 6) fecha.setDate(fecha.getDate() + 2);
    if (dia === 0) fecha.setDate(fecha.getDate() + 1);
    return fecha;
  }

  async guardarSimulacion() {
    if (this.guardandoLoading || !this.prospectCompanyId) return;
    this.guardandoLoading = true;
    this.guardandoStep = 'validando';
    try {
      const hoy = this.formatDateCL(new Date());
      const vencimiento = this.formatDateCL(this.getFechaVencimientoDate());

      const step1Payload = {
        tasa: this.effectiveMonthlyRatePercent,
        anticipo: this.advancePercent,
        gasto: this.gastosOperacionales,
        iva: this.vatCost,
        comision: COMM_RATE * 100,
        monto_apertura_linea: 98500,
        facturas: [[
          this.facturaNumero || '99999',
          this.businessName.toLowerCase(),
          this.deudorRutStatus === 'valid' ? this.deudorRut : this.rut,
          hoy,
          vencimiento,
          String(this.invoiceAmount),
          '33',
        ]],
      };

      const step1Res: any = await this.settings.get_query(2, `simulaciones/validarfacturasSimulacion/${this.prospectCompanyId}/`, step1Payload);
      if (step1Res?.status !== 200) return;

      this.guardandoStep = 'guardando';

      const infoItems: any[] = (step1Res?._body?.success?.info ?? []).map((item: any) => ({
        ...item,
        dias: this.termDays,
      }));

      const tasaMensual = this.effectiveMonthlyRatePercent / 100;
      const tasaDiaria = tasaMensual / 30;
      const interes_total = Math.round(this.financedAmount * tasaDiaria * this.termDays);

      const formatCLP = (n: number) =>
        n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

      const step2Payload = {
        tasa: this.effectiveMonthlyRatePercent,
        anticipo: this.advancePercent,
        gastos: this.gastosOperacionales,
        comision: this.commissionCost,
        monto_apertura_linea: 98500,
        status: 2,
        show_tasa: false,
        show_apertura_linea: false,
        convenio: false,
        create_by_client: false,
        is_confirming: false,
        cxc: '',
        comentario_cuenta: null,
        total: formatCLP(this.invoiceAmount),
        valor_anticipo_total: formatCLP(this.financedAmount),
        interes_total,
        arr_documentos: infoItems,
        documentos_aplicados: [],
        cuentas_aplicadas: [],
        descuentos: [],
      };

      const step2Res: any = await this.settings.get_query(2, `simulaciones/ingram-crear-simulacion/${this.prospectCompanyId}/`, step2Payload);
      if (step2Res?.status === 200) {
        this.simulacionGuardadaId = step2Res?._body?.success?.simulation_id ?? null;
        this.settings.snackBar.open('¡Simulación creada con éxito!', 'Cerrar', {
          duration: 5000,
          panelClass: 'simpli-snack',
        });
      }
    } catch {
      // silently fail
    } finally {
      this.guardandoLoading = false;
      this.guardandoStep = 'idle';
    }
  }

  async descargarSimulacion() {
    if (this.descargandoLoading || !this.simulacionGuardadaId) return;
    this.descargandoLoading = true;
    try {
      const res: any = await this.settings.get_query(1, `simulaciones/generate-pdf/${this.simulacionGuardadaId}/`);
      const url: string = res?._body?.success?.info ?? '';
      if (url) window.open(url, '_blank');
    } catch {
      // silently fail
    } finally {
      this.descargandoLoading = false;
    }
  }

  simularOtraRut() {
    this.showRutStep = true;
    this.rut = '';
    this.rutStatus = 'idle';
    this.rutErrorMsg = '';
    this.tramoMessage = '';
    this.businessName = '';
    this.prospectResult = null;
    this.prospectCompanyId = null;
    this.facturaNumero = '';
    this.simulacionGuardadaId = null;
    this.showOnboardingForm = false;
    this.onboardingRegistrado = false;
    this.onboardingPortalEmail = '';
    this.onboardingPortalPassword = '';
    this.tramoMessage = '';
    this.tramoNumber = null;
    this.tramoRangeMin = null;
    this.tramoRangeMax = null;
    this.isPersonaNatural = false;
    this.deudorRut = '';
    this.deudorRutStatus = 'idle';
    this.promedioDias = null;
    this.lastValidatedDeudorRut = '';
  }

  onDeudorRutInput(raw: string) {
    this.deudorRut = this.formatRut(raw);
    if (!this.deudorRut || this.deudorRut.length < 3) {
      this.deudorRutStatus = 'idle';
      this.promedioDias = null;
      return;
    }
    if (!this.isValidRut(this.deudorRut)) {
      this.deudorRutStatus = 'invalid';
      this.promedioDias = null;
      return;
    }
    if (this.deudorRut === this.lastValidatedDeudorRut) return;
    this.lastValidatedDeudorRut = this.deudorRut;
    this.loadPromedioDias();
  }

  private async loadPromedioDias() {
    this.deudorRutStatus = 'loading';
    try {
      const res: any = await this.settings.get_query(1, `cobranza/promedioDiasPagoDeudor/?rut=${this.deudorRut}`);
      const dias = res._body?.success?.promedio_dias ?? 0;
      this.promedioDias = dias;
      this.deudorRutStatus = 'valid';
      this.invalidarSimulacion();
      this.setTermDays(Math.max(30, dias));
    } catch {
      this.deudorRutStatus = 'valid';
      this.promedioDias = 0;
      this.invalidarSimulacion();
      this.setTermDays(30);
    }
  }

  salir() {
    localStorage.clear();
    location.replace(SettingsService.lemu_base + 'login?alianza=ingram');
  }

  hazteCliente() {
    const ruta = SettingsService.lemu_base + 'registro-empresa';
    location.replace(ruta);
  }

  ingresar() {
    const ruta = SettingsService.lemu_base + 'login';
    location.replace(ruta);
  }

  explorarApp() {
    const plataforma = this.getPlatform();
    if (plataforma === 'Mac' || plataforma === 'iOS') {
      window.open('https://apps.apple.com/cl/app/simpli-factoring/id6740271087', '_blank');
    } else {
      window.open('https://play.google.com/store/apps/details?id=app.simplilatam.com&pcampaignid=web_share', '_blank');
    }
  }

  getPlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/windows nt/.test(userAgent)) return 'Windows';
    if (/macintosh|mac os x/.test(userAgent)) return 'Mac';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'iOS';
    if (/android/.test(userAgent)) return 'Android';
    if (/linux/.test(userAgent)) return 'Linux';
    return 'Desconocido';
  }
}
